import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { title: "", category: "business", excerpt: "", content: "", cover_image: "", author: "people, redefined.", featured: false, published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/blog", form);
      else await api.put(`/admin/blog/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted"); load();
  };

  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne";

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-espresso">Blog</h1>
          <p className="text-espresso/60 mt-2">{posts.length} posts</p>
        </div>
        <button onClick={openNew} data-testid="blog-new" className="inline-flex items-center gap-2 bg-espresso text-ivory rounded-full px-5 py-2.5 text-sm font-medium hover:bg-forest transition-colors">
          <Plus size={16} /> New Post
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-taupe rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`blog-row-${p.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
              <div className="min-w-0">
                <h3 className="font-serif text-lg text-espresso truncate">{p.title}</h3>
                <p className="text-espresso/60 text-sm">{p.category === "business" ? "Business" : "Career"} {p.featured && "· Featured"} {!p.published && "· Draft"}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="p-2 rounded-lg bg-ivory hover:bg-champagne transition-colors"><Edit size={16} className="text-espresso" /></button>
              <button onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`} className="p-2 rounded-lg bg-ivory hover:bg-red-200 transition-colors"><Trash2 size={16} className="text-espresso" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-espresso/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="blog-form" className="bg-ivory rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-espresso">{editing === "new" ? "New Post" : "Edit Post"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X className="text-espresso" /></button>
            </div>
            <div className="space-y-4">
              <input required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" className={inputCls} data-testid="blog-title" />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="blog-category">
                  <option value="business">Business Insights</option>
                  <option value="career">Career Insights</option>
                </select>
                <input value={form.author} onChange={(e) => set("author", e.target.value)} placeholder="Author" className={inputCls} />
              </div>
              <input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="Cover image URL" className={inputCls} data-testid="blog-cover" />
              <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="Excerpt" rows={2} className={inputCls} data-testid="blog-excerpt" />
              <textarea required value={form.content} onChange={(e) => set("content", e.target.value)} placeholder="Content (separate paragraphs with blank lines)" rows={8} className={inputCls} data-testid="blog-content" />
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
              </div>
              <button type="submit" data-testid="blog-save" className="bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors">Save Post</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
